import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import {
  ProbabilisticBundleDesign,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { LinkBox, LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { RainbowCircle } from '@sorare/core/src/components/rewards/Banner/RainbowCircle';
import { ANY_SPORT_PLAY_INVENTORY_BOXES_TYPE } from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';

import { RewardBoxDialog } from 'components/rewards/RewardBoxDialog';

import { RewardBoxBanner_ProbabilisticBundle } from './__generated__/index.graphql';
import crateUrl from './assets/crate.png';

const Banner = styled(LinkBox)`
  display: inline-grid;
  grid-template-columns: max-content 1fr;
  grid-template-areas:
    'icon title'
    'icon cta';
  align-items: center;
  height: min-content;
  column-gap: var(--intermediate-unit);
  &.withMargin {
    margin: var(--triple-unit) 0;
  }
`;
const StyledButton = styled(LinkOverlay)`
  grid-area: cta;
  &,
  &:hover,
  &:focus {
    color: var(--c-neutral-100);
  }
`;
const RainbowWrapper = styled.div`
  grid-area: icon;
  width: calc(8 * var(--unit));
  img {
    width: var(--quadruple-unit);
  }
`;

type Props = {
  rewardBoxes: RewardBoxBanner_ProbabilisticBundle[];
  count: number;
};
export const RewardBoxBanner = ({ rewardBoxes, count }: Props) => {
  const { sport = Sport.FOOTBALL, generateSportContextPath } =
    useSportContext();
  const [open, setOpen] = useState(false);

  const [showBanner, setShowBanner] = useState(rewardBoxes.length > 0);

  const unclaimedBoxes = rewardBoxes.filter(rb => !rb.opened);

  if (rewardBoxes.length > 0 && showBanner === false) {
    setShowBanner(true);
  }

  if (!showBanner) {
    return null;
  }

  return (
    <>
      {unclaimedBoxes.length > 0 && (
        <Banner>
          <RainbowWrapper>
            <RainbowCircle colors={['#00f', '#ccf', '#006']}>
              <img src={crateUrl} alt="" />
            </RainbowCircle>
          </RainbowWrapper>
          <FormattedMessage
            id="RewardBox.banner.title"
            defaultMessage="{nb, plural, one {One reward box to open} other {# reward boxes to open}}"
            values={{ nb: count }}
          />
          <StyledButton
            {...([Sport.FOOTBALL, Sport.NBA].includes(sport)
              ? {
                  as: Link,
                  to: generateSportContextPath(
                    ANY_SPORT_PLAY_INVENTORY_BOXES_TYPE,
                    {
                      params: {
                        type:
                          unclaimedBoxes[0].config.design ===
                          ProbabilisticBundleDesign.PRO
                            ? unclaimedBoxes[0].config.rarity
                            : 'rivals',
                      },
                    }
                  ),
                }
              : { onClick: () => setOpen(true) })}
          >
            <Chip>
              <FormattedMessage
                id="RewardBox.banner.cta"
                defaultMessage="Open Now"
              />
            </Chip>
          </StyledButton>
        </Banner>
      )}
      <RewardBoxDialog
        rewardBoxes={rewardBoxes}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

RewardBoxBanner.fragments = {
  ProbabilisticBundle: gql`
    fragment RewardBoxBanner_ProbabilisticBundle on ProbabilisticBundle {
      id
      config {
        id
        design
        rarity
      }
      ...RewardBoxDialog_ProbabilisticBundle
    }
    ${RewardBoxDialog.fragments.ProbabilisticBundle}
  ` as TypedDocumentNode<RewardBoxBanner_ProbabilisticBundle>,
};
